<!-- Notifications toggle -->
<button
    mat-icon-button
    (click)="openPanel()"
    #notificationsOrigin
    class="relative">
    <ng-container *ngIf="unreadCount > 0">
        <span class="absolute -top-1 -right-1 z-10 flex items-center justify-center">
            <span
                class="flex items-center justify-center shrink-0 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-xs font-medium">
                {{unreadCount}}
            </span>
        </span>
    </ng-container>
    <mat-icon [svgIcon]="'heroicons_outline:bell'"></mat-icon>
</button>

<!-- Notifications panel -->
<ng-template #notificationsPanel>

    <div
        class="fixed inset-0 sm:static sm:inset-auto flex flex-col sm:min-w-90 sm:w-90 sm:rounded-2xl overflow-hidden shadow-lg">

        <!-- Header -->
        <div class="flex shrink-0 items-center py-4 pr-4 pl-6 bg-primary text-on-primary">
            <div class="sm:hidden -ml-1 mr-3">
                <button
                    mat-icon-button
                    (click)="closePanel()">
                    <mat-icon
                        class="icon-size-5 text-current"
                        [svgIcon]="'heroicons_solid:x-mark'"></mat-icon>
                </button>
            </div>
            <div class="text-lg font-medium leading-10">Alertas</div>
            <div class="ml-auto">
                <button
                    mat-icon-button
                    [matTooltip]="'Marcar todas como leídas'"
                    [disabled]="recentAlerts.length === 0"
                    (click)="markAllAlertsAsRead()">
                    <mat-icon
                        class="icon-size-5 text-current"
                        [svgIcon]="'heroicons_solid:envelope-open'"></mat-icon>
                </button>
            </div>
        </div>

        <!-- Content -->
        <div class="relative flex flex-col flex-auto sm:max-h-120 divide-y overflow-y-auto bg-card">
            
            <!-- Recent Unified Alerts (Top 5 Unread) -->
            <ng-container *ngFor="let alert of recentAlerts; trackBy: trackByFn">
                <div class="flex group hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5">
                    <!-- Alert Link -->
                    <a
                        class="flex flex-auto py-4 pl-6 cursor-pointer"
                        [routerLink]="['/alerts']"
                        [queryParams]="{id: alert.id, source: alert.source}"
                        (click)="closePanel()">
                        
                        <!-- Severity Icon with Color -->
                        <div
                            class="flex shrink-0 items-center justify-center w-10 h-10 mr-4 rounded-full"
                            [ngClass]="getSeverityColor(alert.severity)">
                            <mat-icon
                                class="icon-size-5 text-white"
                                [svgIcon]="getSeverityIcon(alert.severity)">
                            </mat-icon>
                        </div>
                        
                        <!-- Alert Details -->
                        <div class="flex flex-col flex-auto">
                            <!-- Alert Type/Title -->
                            <div class="font-semibold line-clamp-1 flex items-center gap-2">
                                <span>{{ getAlertTypeDisplay(alert.alert_type) }}</span>
                                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                      [ngClass]="{
                                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': alert.severity === 'critical',
                                          'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200': alert.severity === 'high',
                                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200': alert.severity === 'medium',
                                          'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200': alert.severity === 'low'
                                      }">
                                    {{ alert.severity }}
                                </span>
                            </div>
                            
                            <!-- Alert Message/Description -->
                            <div class="mt-1 text-sm line-clamp-2 text-secondary">
                                {{ getAlertDescription(alert) }}
                            </div>
                            
                            <!-- Metadata -->
                            <div class="mt-2 flex items-center gap-2 text-xs text-secondary">
                                <mat-icon class="icon-size-4">schedule</mat-icon>
                                <span>{{ alert.created_at | date:'MMM dd, h:mm a' }}</span>
                                <span *ngIf="alert.project_id" class="ml-2">
                                    <mat-icon class="icon-size-4">folder</mat-icon>
                                    Proyecto #{{ alert.project_id }}
                                </span>
                            </div>
                        </div>
                    </a>

                    <!-- Quick Actions -->
                    <div class="relative flex flex-col my-4 mr-4 ml-2">
                        <!-- Mark as read -->
                        <button
                            class="w-8 h-8 min-h-8"
                            mat-icon-button
                            (click)="markAlertAsRead(alert); $event.stopPropagation()"
                            [matTooltip]="'Marcar como leída'">
                            <mat-icon
                                class="icon-size-5 text-gray-400 hover:text-primary"
                                [svgIcon]="'heroicons_solid:check-circle'"></mat-icon>
                        </button>
                    </div>
                </div>
            </ng-container>

            <!-- Divider between alerts and notifications -->
            <div *ngIf="recentAlerts.length > 0 && notifications && notifications.length > 0" 
                 class="h-2 bg-gray-100 dark:bg-gray-900"></div>

            <!-- Legacy Notifications (if any) -->
            <ng-container *ngFor="let notification of notifications; trackBy: trackByFn">
                <div
                    class="flex group hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
                    [ngClass]="{'unread': !notification.is_read}">

                    <!-- Notification with a link -->
                    <ng-container *ngIf="notification.link">
                        <!-- Normal links -->
                        <ng-container *ngIf="!notification.useRouter">
                            <a
                                class="flex flex-auto py-5 pl-6 cursor-pointer"
                                [href]="notification.link">
                                <ng-container *ngTemplateOutlet="notificationContent"></ng-container>
                            </a>
                        </ng-container>
                        <!-- Router links -->
                        <ng-container *ngIf="notification.useRouter">
                            <a
                                class="flex flex-auto py-5 pl-6 cursor-pointer"
                                [routerLink]="notification.link"
                                (click)="closePanel()">
                                <ng-container *ngTemplateOutlet="notificationContent"></ng-container>
                            </a>
                        </ng-container>
                    </ng-container>

                    <!-- Notification without a link
                    <ng-container *ngIf="!notification.link">
                        <div class="flex flex-auto py-5 pl-6">
                            <ng-container *ngTemplateOutlet="notificationContent"></ng-container>
                        </div>
                    </ng-container>-->

                    <!-- Actions -->
                    <div class="relative flex flex-col my-5 mr-6 ml-2">
                        <!-- Indicator -->
                        <button
                            class="w-6 h-6 min-h-6"
                            mat-icon-button
                            (click)="toggleRead(notification)"
                            [matTooltip]="notification.is_read ? 'Mark as unread' : 'Mark as read'">
                            <span
                                class="w-2 h-2 rounded-full"
                                [ngClass]="{'bg-gray-400 dark:bg-gray-500 sm:opacity-0 sm:group-hover:opacity-100': notification.is_read,
                                            'bg-primary': !notification.is_read}"></span>
                        </button>
                        <!-- Remove -->
                        <button
                            class="w-6 h-6 min-h-6 sm:opacity-0 sm:group-hover:opacity-100"
                            mat-icon-button
                            (click)="delete(notification)"
                            [matTooltip]="'Remove'">
                            <mat-icon
                                class="icon-size-4"
                                [svgIcon]="'heroicons_solid:x-mark'"></mat-icon>
                        </button>
                    </div>

                </div>

                <!-- Notification content template -->
                <ng-template #notificationContent>
                    <!-- Icon -->
                    <ng-container>
                        <div
                            class="flex shrink-0 items-center justify-center w-8 h-8 mr-4 rounded-full bg-gray-100 dark:bg-gray-700">
                            <mat-icon
                                class="icon-size-5"
                            >{{notification.priority === 'high' ? 'error' : 'warning'}}
                            </mat-icon>
                        </div>
                    </ng-container>
                    <!-- Image -->
                    <ng-container *ngIf="notification.image">
                        <img
                            class="shrink-0 w-8 h-8 mr-4 rounded-full overflow-hidden object-cover object-center"
                            [src]="notification.image"
                            [alt]="'Notification image'">
                    </ng-container>
                    <!-- Title, description & time -->
                    <div class="flex flex-col flex-auto">
                        <ng-container *ngIf="notification.title">
                            <div
                                class="font-semibold line-clamp-1"
                                [innerHTML]="notification.title"></div>
                        </ng-container>
                        <ng-container *ngIf="notification.description">
                            <div
                                class="line-clamp-2">{{notification.description}}</div>
                        </ng-container>
                        <ng-container *ngIf="notification.id">
                            <div
                                class="line-clamp-2">Alerta en Proyecto {{notification.project_id }}</div>
                        </ng-container>
                        <div class="mt-2 text-sm leading-none text-secondary">
                            {{notification.datetime | date:'MMM dd, h:mm a'}} | HT: {{notification.honey_token_id}}
                        </div>
                    </div>
                </ng-template>
            </ng-container>

            <!-- No alerts or notifications -->
            <ng-container *ngIf="(!recentAlerts || recentAlerts.length === 0) && (!notifications || notifications.length === 0)">
                <div class="flex flex-col flex-auto items-center justify-center sm:justify-start py-12 px-8">
                    <div
                        class="flex flex-0 items-center justify-center w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-600">
                        <mat-icon
                            class="text-primary-700 dark:text-primary-50"
                            [svgIcon]="'heroicons_outline:bell'"></mat-icon>
                    </div>
                    <div class="mt-5 text-2xl font-semibold tracking-tight">Sin alertas</div>
                    <div class="w-full max-w-60 mt-1 text-md text-center text-secondary">
                        Cuando tengas alertas nuevas, aparecerán aquí.
                    </div>
                </div>
            </ng-container>

        </div>

        <!-- Footer: View All + Stats -->
        <div class="flex flex-col gap-2 py-3 px-6 border-t bg-gray-50 dark:bg-gray-800">
            <!-- Stats Summary -->
            <div *ngIf="totalUnreadAlerts > 5" class="flex items-center justify-center gap-2 text-xs text-secondary">
                <mat-icon class="icon-size-4">info</mat-icon>
                <span>{{ totalUnreadAlerts - recentAlerts.length }} alertas más sin leer</span>
            </div>
            
            <!-- View All Link -->
            <a 
                [routerLink]="['/alerts']"
                (click)="closePanel()"
                class="flex items-center justify-center text-sm font-medium text-primary hover:underline">
                <mat-icon class="icon-size-5 mr-2">list_alt</mat-icon>
                Ver todas las alertas
            </a>
        </div>

    </div>

</ng-template>
